import React from 'react';
import { logError } from '@utils/usefulFuncs';
import { DocumentVaultFilterContext } from '@/contexts/DocumentVaultFilterContext';

const defaultState = {
  selectedIds: new Set(),
  isSelectAllMatching: false,
  selectedCount: 0,
  isSelected: () => false,
  toggleDocument: () => logError('toggleDocument called without a DocumentVaultSelectionProvider'),
  setPageSelection: () => logError('setPageSelection called without a DocumentVaultSelectionProvider'),
  selectAllMatching: () => logError('selectAllMatching called without a DocumentVaultSelectionProvider'),
  clearSelection: () => logError('clearSelection called without a DocumentVaultSelectionProvider'),
  resolveSelectedDocs: () => [],
};

export const DocumentVaultSelectionContext = React.createContext(defaultState);

/*
  Selection lives outside the filter reducer because it answers a different question: the filters
  say what is on screen, the selection says what an action will run against.

  `isSelectAllMatching` is a mode rather than an enumeration of ids, so it survives paging and
  refetches and resolves against the current filtered set at the moment an action fires.
*/
export const DocumentVaultSelectionProvider = ({ children, filteredDocs }) => {
  const { filters } = React.useContext(DocumentVaultFilterContext);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [isSelectAllMatching, setIsSelectAllMatching] = React.useState(false);

  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectAllMatching(false);
  }, []);

  // Narrowing the results clears the selection: acting on documents the user can no longer see
  // is exactly how a bulk download surprises someone.
  const narrowingKey = JSON.stringify([
    filters.search,
    filters.tickers,
    filters.docTypes,
    filters.tags,
    filters.statuses,
    filters.fileFormats,
    filters.cleanEligibleOnly,
    filters.dateRange,
  ]);

  const previousNarrowingKeyRef = React.useRef(narrowingKey);
  React.useEffect(() => {
    if (previousNarrowingKeyRef.current === narrowingKey) return;
    previousNarrowingKeyRef.current = narrowingKey;
    clearSelection();
  }, [narrowingKey, clearSelection]);

  // Paging and re-sorting deliberately do not clear: the same documents are still in play.

  const isSelected = React.useCallback(
    (doc) => isSelectAllMatching || selectedIds.has(doc?.id),
    [isSelectAllMatching, selectedIds]
  );

  const toggleDocument = React.useCallback((doc) => {
    setIsSelectAllMatching(false);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(doc.id)) {
        next.delete(doc.id);
      } else {
        next.add(doc.id);
      }
      return next;
    });
  }, []);

  const setPageSelection = React.useCallback((docs, shouldSelect) => {
    setIsSelectAllMatching(false);
    setSelectedIds((current) => {
      const next = new Set(current);
      docs.forEach((doc) => {
        if (shouldSelect) {
          next.add(doc.id);
        } else {
          next.delete(doc.id);
        }
      });
      return next;
    });
  }, []);

  const selectAllMatching = React.useCallback(() => {
    setIsSelectAllMatching(true);
    setSelectedIds(new Set());
  }, []);

  const resolveSelectedDocs = React.useCallback(() => {
    if (isSelectAllMatching) return filteredDocs || [];
    return (filteredDocs || []).filter((doc) => selectedIds.has(doc.id));
  }, [filteredDocs, isSelectAllMatching, selectedIds]);

  const selectedCount = isSelectAllMatching ? (filteredDocs || []).length : selectedIds.size;

  const value = React.useMemo(
    () => ({
      selectedIds,
      isSelectAllMatching,
      selectedCount,
      isSelected,
      toggleDocument,
      setPageSelection,
      selectAllMatching,
      clearSelection,
      resolveSelectedDocs,
    }),
    [
      selectedIds,
      isSelectAllMatching,
      selectedCount,
      isSelected,
      toggleDocument,
      setPageSelection,
      selectAllMatching,
      clearSelection,
      resolveSelectedDocs,
    ]
  );

  return <DocumentVaultSelectionContext.Provider value={value}>{children}</DocumentVaultSelectionContext.Provider>;
};
